/**
 * Tableaux – portage de design/components/bundle.js (DataTable, TableSettings).
 * La fenêtre de réglages est une fenêtre non modale Radix Dialog, affichée à sa place dans le tableau (même DOM, mêmes
 * classes) : Échap ou clic à côté pour la fermer, focus placé dans la fenêtre à l'ouverture et rendu à l'engrenage à la
 * fermeture ; Colonnes / Tri / Filtres sont de vrais onglets (Radix Tabs) à l'apparence du contrôle segmenté.
 * Grandes listes : au-delà de SEUIL_VIRTUALISATION lignes, seules les lignes visibles sont rendues (TanStack Virtual).
 * Chargement : `loading` sans ligne → lignes squelettes qui reprennent les colonnes (squelette.ts).
 */
import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { defaultRangeExtractor, elementScroll, useVirtualizer, type Range } from '@tanstack/react-virtual';
import { cx, h, Icon } from './base';
import { SegmentedControl } from './formulaires';
import { Skeleton, statutChargement } from './squelette';
// Styles du tableau virtualisé (ajout AGORA, sans effet sous le seuil).
import './donnees.css';

/** Vrai à l'intérieur de la fenêtre de réglages d'un DataTable (TableSettings devient alors son contenu Radix). */
const DansReglages = React.createContext(false);

/* ---------- DataTable ---------- */
/**
 * Grandes listes (ajout AGORA, 26/10/2026) : au-delà de ce nombre de lignes, le DataTable n'affiche que les lignes
 * visibles (TanStack Virtual) dans une zone défilante de hauteur bornée, à en-tête collant. Jusqu'au seuil, le tableau
 * est rendu tel quel (DOM du design, inchangé). Prop `virtualize` : `true` pour forcer, `false` pour désactiver.
 * Règles, accessibilité et limites : design/components/DataTable/README.md et web/README.md › « Grandes listes ».
 */
export const SEUIL_VIRTUALISATION = 200;
/** Lignes rendues hors de la zone visible, de chaque côté : défilement sans blanc, Tab trouve toujours la ligne suivante. */
const LIGNES_HORS_ECRAN = 10;
/** `onEndReached` est appelé quand la dernière ligne visible est à moins de ce nombre de lignes de la fin. */
const LIGNES_AVANT_FIN = 20;
/** Hauteur estimée d'une ligne (px, corrigée par la mesure de chaque ligne rendue) : tableau dense / normal. */
const HAUTEUR_LIGNE = { dense: 36, normale: 46 };
/**
 * Largeur minimale (px) d'une colonne sans `width` dans un tableau virtualisé (largeurs figées par l'en-tête) : en
 * dessous, la zone défile horizontalement au lieu d'écraser les colonnes.
 */
const LARGEUR_MIN_COLONNE = 140;

export interface DataTableColumn {
  key: string; label: string; width?: number; align?: 'start' | 'end' | 'center'; mono?: boolean;
  /** Colonne selon laquelle la liste est triée (`aria-sort` et flèche sur son en-tête), triable au clic ou non. */
  sort?: 'ascending' | 'descending';
  /**
   * Ajout AGORA (Issue #198, décision du DSI) : en-tête cliquable pour trier, avec `onSort`. Dès qu'une colonne déclare
   * `sortable: true`, seules ces colonnes (celles que l'API sait trier) ont un en-tête bouton ; sans aucune colonne
   * `sortable: true`, `onSort` rend triables toutes les colonnes sauf `sortable: false` (comportement d'origine, gardé
   * pour les usages existants).
   */
  sortable?: boolean;
}
export interface DataTableProps {
  settings?: { label: string; modifiedLabel: string; modified?: boolean; open?: boolean; onClick?: () => void; panel?: React.ReactNode };
  columns: DataTableColumn[]; rows: (Record<string, React.ReactNode> & { id?: string })[];
  selectable?: boolean; selectedIds?: string[]; dense?: boolean; toolbar?: React.ReactNode; footer?: React.ReactNode;
  /**
   * Ajouts AGORA : clic sur une ligne, tri au clic sur l'en-tête d'une colonne triable (`sortable`, voir
   * DataTableColumn), message si aucune ligne.
   */
  onRowClick?: (id: string) => void; onSort?: (key: string) => void; emptyLabel?: string; ariaLabel?: string;
  /**
   * Ajouts AGORA – grandes listes : `virtualize` (par défaut, virtualisé au-delà de SEUIL_VIRTUALISATION lignes ;
   * `true` / `false` pour forcer ou désactiver) ; `onEndReached` (tableau virtualisé seulement : la fin approche, charger
   * la page suivante – appelé une fois par nombre de lignes ; le bouton « Afficher plus » du pied reste la voie au clavier).
   */
  virtualize?: boolean; onEndReached?: () => void;
  /**
   * Ajouts AGORA – chargement (squelette, 26/10/2026) : `loading` vrai et aucune ligne → `loadingRows` lignes
   * squelettes (6 par défaut) qui reprennent les colonnes, à la place de `emptyLabel` ; le tableau porte
   * `aria-busy="true"` et `loadingLabel` (traduit, `app.chargement`) est lu une fois par les lecteurs d'écran (statut
   * visuellement masqué, à côté du tableau). Sans effet quand des lignes sont affichées (page suivante d'une liste :
   * « Afficher plus » reste la voie) ; `loading` faux : DOM inchangé.
   */
  loading?: boolean; loadingLabel?: string; loadingRows?: number;
}
type LigneTableau = DataTableProps['rows'][number];

/**
 * Colonnes dont l'en-tête est un bouton de tri (Issue #198) : aucune sans `onSort` ; avec `onSort`, les colonnes
 * `sortable: true` s'il y en a, sinon toutes sauf `sortable: false` (comportement d'origine).
 */
function colonnesTriables(p: DataTableProps, cols: DataTableColumn[]): Set<string> {
  if (!p.onSort) return new Set();
  const choisies = cols.some((c) => c.sortable === true);
  return new Set(cols.filter((c) => (choisies ? c.sortable === true : c.sortable !== false)).map((c) => c.key));
}

/**
 * En-tête : même DOM avec ou sans virtualisation ; virtualisé, il est mesuré et porte aria-rowindex="1". `aria-sort`
 * suit `sort`, que la colonne soit triable au clic ou non (liste triée par une liste « Trier par » de l'écran).
 */
function enTete(p: DataTableProps, cols: DataTableColumn[], mesure?: React.Ref<HTMLTableSectionElement>) {
  const triables = colonnesTriables(p, cols);
  return h('thead', mesure ? { ref: mesure } : null, h('tr', mesure ? { 'aria-rowindex': 1 } : null,
    p.selectable ? h('th', { style: { width: 36 } }, h('span', { className: 'ag-check' })) : null,
    cols.map((c) => h('th', { key: c.key, style: { width: c.width, textAlign: c.align || 'start' }, 'aria-sort': c.sort, scope: 'col' },
      triables.has(c.key) ? h('button', { type: 'button', className: 'agx-th-sort', onClick: () => p.onSort?.(c.key) }, c.label) : c.label,
      c.sort ? h('span', { className: 'ag-sort' }, c.sort === 'ascending' ? ' ↑' : ' ↓') : null))));
}

/** Ligne : même DOM avec ou sans virtualisation ; virtualisée, elle porte son index (mesure) et aria-rowindex. */
function ligne(p: DataTableProps, cols: DataTableColumn[], r: LigneTableau, ri: number, choisies: Set<string>, mesure?: (el: HTMLTableRowElement | null) => void) {
  const sel = choisies.has(String(r.id));
  return h('tr', {
    key: r.id || ri, 'aria-selected': sel || undefined,
    className: p.onRowClick ? 'agx-tr-click' : undefined,
    onClick: p.onRowClick && r.id ? () => p.onRowClick?.(String(r.id)) : undefined,
    ...(mesure ? { ref: mesure, 'data-index': ri, 'aria-rowindex': ri + 2 } : null),
  },
  p.selectable ? h('td', null, h('span', { className: cx('ag-check', sel && 'ag-check-on') }, sel ? h(Icon, { name: 'check', size: 12 }) : null)) : null,
  cols.map((c) => h('td', { key: c.key, className: c.mono ? 'ag-mono' : undefined, style: { textAlign: c.align || 'start' } }, r[c.key])));
}

/** Lignes squelettes par défaut d'un tableau en chargement (une demi-page d'une liste dense). */
const LIGNES_SQUELETTE = 6;
/** Largeurs (%) des formes d'une cellule squelette, variées d'une ligne et d'une colonne à l'autre (toujours les mêmes). */
const LARGEURS_SQUELETTE = [72, 48, 86, 60, 78, 54];

/** Ligne squelette (chargement) : une forme par colonne, alignée comme la colonne ; décorative (aria-hidden). */
function ligneSquelette(p: DataTableProps, cols: DataTableColumn[], ri: number) {
  return h('tr', { key: `squelette-${ri}`, className: 'agx-table-squelette', 'aria-hidden': true },
    p.selectable ? h('td', null) : null,
    cols.map((c, ci) => h('td', { key: c.key, style: { textAlign: c.align || 'start' } },
      h(Skeleton, { width: LARGEURS_SQUELETTE[(ri * 2 + ci) % LARGEURS_SQUELETTE.length] }))));
}

const mouvementReduit = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
/** Défilement programmé de la zone : jamais animé quand l'utilisateur demande moins de mouvement (prefers-reduced-motion). */
const defiler: typeof elementScroll = (offset, options, instance) =>
  elementScroll(offset, { ...options, behavior: options.behavior === 'smooth' && mouvementReduit() ? 'instant' : options.behavior }, instance);

/** Espace réservé aux lignes non rendues (au-dessus, entre la ligne focalisée et la zone visible, en dessous). */
const espace = (cle: string, hauteur: number, colonnes: number) =>
  h('tr', { key: cle, className: 'agx-table-espace', 'aria-hidden': true }, h('td', { colSpan: colonnes, style: { height: hauteur } }));

/**
 * Tableau virtualisé (au-delà du seuil) : zone défilante bornée (région nommée comme le tableau, atteignable au clavier),
 * en-tête collant dans le même <table> (colonnes alignées, `table-layout: fixed`), lignes non rendues remplacées par des
 * espaces masqués. aria-rowcount = total des lignes (en-tête compris), aria-rowindex sur chaque ligne rendue.
 */
function TableVirtuelle({ p, cols, rows, choisies, depart }: { p: DataTableProps; cols: DataTableColumn[]; rows: LigneTableau[]; choisies: Set<string>; depart: number }) {
  const zone = React.useRef<HTMLDivElement>(null);
  const entete = React.useRef<HTMLTableSectionElement>(null);
  const [hauteurEntete, setHauteurEntete] = React.useState(0);
  // Ligne qui contient le focus (lien, bouton, liste de choix) : gardée rendue quand elle sort de la zone visible, sinon
  // le focus retomberait sur la page au premier coup de molette.
  const focalisee = React.useRef<number | null>(null);
  const rangeExtractor = React.useCallback((r: Range) => {
    const indexes = defaultRangeExtractor(r);
    const f = focalisee.current;
    return f === null || f >= r.count || indexes.includes(f) ? indexes : [...indexes, f].sort((a, b) => a - b);
  }, []);
  const hauteurLigne = p.dense ? HAUTEUR_LIGNE.dense : HAUTEUR_LIGNE.normale;
  const estimateSize = React.useCallback(() => hauteurLigne, [hauteurLigne]);
  const getItemKey = React.useCallback((i: number) => rows[i]?.id || i, [rows]);
  const v = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length, getScrollElement: () => zone.current, estimateSize, getItemKey, rangeExtractor,
    overscan: LIGNES_HORS_ECRAN, paddingStart: hauteurEntete, scrollPaddingStart: hauteurEntete, scrollToFn: defiler,
  });

  // Hauteur de l'en-tête collant : décalage des lignes et marge de défilement (une ligne n'est jamais cachée dessous).
  React.useLayoutEffect(() => {
    const el = entete.current;
    if (!el) return;
    const mesurer = () => setHauteurEntete(el.offsetHeight);
    mesurer();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(mesurer);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Passage au mode virtualisé par « Afficher plus » : la zone s'ouvre sur la première ligne ajoutée.
  const reprise = React.useRef(depart);
  React.useLayoutEffect(() => {
    if (!hauteurEntete || reprise.current <= 0) return;
    v.scrollToIndex(Math.min(reprise.current, rows.length - 1), { align: 'start' });
    reprise.current = 0;
  }, [hauteurEntete, rows.length, v]);
  // Nouveau tri : retour en haut de la liste (sans animation).
  const tri = cols.map((c) => (c.sort ? `${c.key}:${c.sort}` : '')).join();
  const triAffiche = React.useRef(tri);
  React.useLayoutEffect(() => {
    if (triAffiche.current === tri) return;
    triAffiche.current = tri;
    v.scrollToOffset(0);
  }, [tri, v]);

  const items = v.getVirtualItems();
  // Chargement de la suite : une seule demande par nombre de lignes (l'écran passe onEndReached tant qu'il reste des pages).
  const finVisible = v.range ? v.range.endIndex : -1;
  const demandee = React.useRef(-1);
  const onEndReached = p.onEndReached;
  React.useEffect(() => {
    if (!onEndReached || finVisible < 0 || finVisible < rows.length - 1 - LIGNES_AVANT_FIN || demandee.current === rows.length) return;
    demandee.current = rows.length;
    onEndReached();
  }, [finVisible, rows.length, onEndReached]);

  const colonnes = cols.length + (p.selectable ? 1 : 0);
  const largeurMin = cols.reduce((t, c) => t + (c.width || LARGEUR_MIN_COLONNE), p.selectable ? 36 : 0);
  const corps: React.ReactNode[] = [];
  let fin = hauteurEntete;
  for (const it of items) {
    if (it.start > fin) corps.push(espace(`espace-${it.index}`, it.start - fin, colonnes));
    corps.push(ligne(p, cols, rows[it.index], it.index, choisies, v.measureElement));
    fin = it.end;
  }
  const total = v.getTotalSize();
  if (total > fin) corps.push(espace('espace-fin', total - fin, colonnes));

  return h('div', {
    ref: zone, className: 'agx-table-defilante', tabIndex: 0, role: p.ariaLabel ? 'region' : undefined, 'aria-label': p.ariaLabel,
    style: { scrollPaddingBlockStart: hauteurEntete },
    onFocus: (e: React.FocusEvent) => {
      const tr = (e.target as Element).closest('tr[data-index]');
      focalisee.current = tr ? Number(tr.getAttribute('data-index')) : null;
    },
    onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
      if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) focalisee.current = null;
    },
  },
  h('table', { className: cx('ag-table', p.dense && 'ag-table-dense'), style: { minWidth: largeurMin }, 'aria-label': p.ariaLabel, 'aria-rowcount': rows.length + 1 },
    enTete(p, cols, entete),
    h('tbody', null, corps)));
}

export function DataTable(p: DataTableProps) {
  const cols = p.columns || [];
  const rows = p.rows || [];
  const s = p.settings;
  const ouvert = !!(s && s.panel);
  const choisies = new Set(p.selectedIds || []);
  const virtuel = rows.length > 0 && (p.virtualize ?? rows.length > SEUIL_VIRTUALISATION);
  // Chargement sans ligne : squelette (lignes décoratives), tableau occupé et statut lu une fois.
  const enChargement = !!p.loading && rows.length === 0;
  // Liste qui franchit le seuil par ajout de lignes (« Afficher plus », même première ligne) : la zone défilante
  // s'ouvrira sur la première ligne ajoutée, là où l'utilisateur lisait, plutôt qu'en haut de la liste.
  const avant = React.useRef({ virtuel, n: rows.length, premiere: rows[0]?.id });
  const depart = virtuel && !avant.current.virtuel && avant.current.n > 0 && avant.current.premiere === rows[0]?.id ? avant.current.n : 0;
  React.useEffect(() => { avant.current = { virtuel, n: rows.length, premiere: rows[0]?.id }; });
  const engrenage = s ? h('button', {
    type: 'button', className: cx('ag-table-gear', s.modified && 'ag-table-gear-mod'), 'aria-haspopup': 'dialog', 'aria-expanded': ouvert,
    'aria-label': s.modified ? `${s.label} — ${s.modifiedLabel}` : s.label,
    title: s.modified ? s.modifiedLabel : s.label,
  }, h(Icon, { name: 'settings', size: 18 }), s.modified ? h('i', { className: 'ag-gear-dot' }) : null) : null;
  const tableau = h('div', { className: cx('ag-table-wrap', 'ag-glass', 'ag-glass-strong', ouvert && 'ag-table-wrap-open', virtuel && 'agx-table-virtuelle') },
    p.toolbar || s ? h('div', { className: 'ag-table-bar' }, p.toolbar || h('span', { style: { flex: 1 } }),
      engrenage ? h(Dialog.Trigger, { asChild: true }, engrenage) : null) : null,
    ouvert ? h('div', { className: 'ag-table-pop' }, h(DansReglages.Provider, { value: true }, s!.panel)) : null,
    enChargement ? statutChargement(p.loadingLabel) : null,
    virtuel
      ? h(TableVirtuelle, { p, cols, rows, choisies, depart })
      : h('table', { className: cx('ag-table', p.dense && 'ag-table-dense'), 'aria-label': p.ariaLabel, 'aria-busy': enChargement || undefined },
        enTete(p, cols),
        h('tbody', null,
          enChargement
            ? Array.from({ length: Math.max(1, p.loadingRows ?? LIGNES_SQUELETTE) }, (_, ri) => ligneSquelette(p, cols, ri))
            : rows.length === 0 && p.emptyLabel
              ? h('tr', null, h('td', { colSpan: cols.length + (p.selectable ? 1 : 0), className: 'agx-table-vide' }, p.emptyLabel))
              : rows.map((r, ri) => ligne(p, cols, r, ri, choisies)))),
    p.footer ? h('div', { className: 'ag-table-foot' }, p.footer) : null);
  if (!s) return tableau;
  // Ouverte tant que l'écran fournit `settings.panel` ; engrenage, Échap et clic à côté passent par settings.onClick
  // (bascule de l'écran) – seulement quand l'état demandé diffère de l'état affiché.
  return h(Dialog.Root, { open: ouvert, modal: false, onOpenChange: (o: boolean) => { if (o !== ouvert) s.onClick?.(); } }, tableau);
}

/* ---------- TableSettings (engrenage : colonnes, tri, filtres, vue par défaut) ---------- */
export type OngletReglages = 'columns' | 'sort' | 'filters';
export interface TableSettingsProps {
  title: string; tab?: OngletReglages; tabs: { columns: string; sort: string; filters: string };
  columns?: { key: string; label: string; visible?: boolean; locked?: boolean }[];
  sort?: { label: string; dir: 'asc' | 'desc' }[]; filters?: { label: string; value: string }[];
  addSortLabel?: string; addFilterLabel?: string; removeLabel?: string; closeLabel?: string;
  modified?: boolean; modifiedText: string; defaultText: string; resetLabel: string; saveDefaultLabel: string; standardLabel?: string;
  /** Ajouts AGORA : interactions (la référence est un état figé). */
  onTab?: (t: OngletReglages) => void; onToggleColumn?: (key: string) => void; onClose?: () => void;
  onReset?: () => void; onSaveDefault?: () => void; onStandard?: () => void;
  onSortDir?: (i: number, dir: 'asc' | 'desc') => void; onRemoveSort?: (i: number) => void; onRemoveFilter?: (i: number) => void;
}

export function TableSettings(p: TableSettingsProps) {
  const tab = p.tab || 'columns', T = p.tabs;
  const section = () => {
    if (tab === 'columns') return h('ul', { className: 'ag-ts-list' }, (p.columns || []).map((c) =>
      h('li', { key: c.key, className: cx('ag-ts-row', c.locked && 'ag-ts-locked') },
        h('span', { className: 'ag-ts-grip', 'aria-hidden': true }, h(Icon, { name: 'grip', size: 16 })),
        h('span', {
          className: cx('ag-check', c.visible && 'ag-check-on'), role: 'checkbox', 'aria-checked': !!c.visible, 'aria-disabled': c.locked || undefined,
          'aria-label': c.label, tabIndex: 0,
          onClick: () => { if (!c.locked) p.onToggleColumn?.(c.key); },
          onKeyDown: (e: React.KeyboardEvent) => { if ((e.key === ' ' || e.key === 'Enter') && !c.locked) { e.preventDefault(); p.onToggleColumn?.(c.key); } },
        }, c.visible ? h(Icon, { name: 'check', size: 12 }) : null),
        h('span', { className: 'ag-ts-label' }, c.label),
        c.locked ? h(Icon, { name: 'lock', size: 14, className: 'ag-ts-lock' }) : null)));
    if (tab === 'sort') return h('div', { className: 'ag-ts-stack' }, (p.sort || []).map((so, i) =>
      h('div', { key: i, className: 'ag-ts-sort' }, h('span', { className: 'ag-ts-n' }, i + 1),
        h('button', { type: 'button', className: 'ag-ts-select' }, so.label, h(Icon, { name: 'chevron', size: 14 })),
        h(SegmentedControl, { ariaLabel: so.label, value: so.dir, options: [{ value: 'asc', label: '↑' }, { value: 'desc', label: '↓' }], onChange: (v) => p.onSortDir?.(i, v as 'asc' | 'desc') }),
        h('button', { type: 'button', className: 'ag-page-x', 'aria-label': p.removeLabel, onClick: () => p.onRemoveSort?.(i) }, h(Icon, { name: 'close', size: 14 })))),
    p.addSortLabel ? h('button', { type: 'button', className: 'ag-ts-add' }, h(Icon, { name: 'plus', size: 16 }), p.addSortLabel) : null);
    return h('div', { className: 'ag-ts-stack' }, (p.filters || []).map((f, i) =>
      h('div', { key: i, className: 'ag-ts-filter' }, h('b', null, f.label), h('span', null, f.value),
        h('button', { type: 'button', className: 'ag-page-x', 'aria-label': p.removeLabel, onClick: () => p.onRemoveFilter?.(i) }, h(Icon, { name: 'close', size: 14 })))),
    p.addFilterLabel ? h('button', { type: 'button', className: 'ag-ts-add' }, h(Icon, { name: 'plus', size: 16 }), p.addFilterLabel) : null);
  };
  const pied = () => h('footer', { className: 'ag-ts-foot' },
    h('div', { className: cx('ag-ts-status', p.modified && 'ag-ts-status-mod') }, h('i'), p.modified ? p.modifiedText : p.defaultText),
    h('div', { className: 'ag-ts-actions' },
      h('button', { type: 'button', className: 'ag-btn ag-btn-quiet ag-btn-sm', disabled: !p.modified, onClick: p.onReset }, p.resetLabel),
      h('button', { type: 'button', className: 'ag-btn ag-btn-primary ag-btn-sm', disabled: !p.modified, onClick: p.onSaveDefault }, p.saveDefaultLabel)),
    p.standardLabel ? h('a', { href: '#', className: 'ag-ts-std', onClick: (e: React.MouseEvent) => { e.preventDefault(); p.onStandard?.(); } }, p.standardLabel) : null);
  const dansReglages = React.useContext(DansReglages);
  const titre = h('b', null, p.title);
  const onglets = (['columns', 'sort', 'filters'] as const).map((v) =>
    h(Tabs.Trigger, { key: v, value: v, className: cx('ag-seg-opt', tab === v && 'ag-seg-on') }, T[v]));
  // Onglets Radix (←/→ entre Colonnes, Tri, Filtres) à l'apparence du contrôle segmenté : même DOM que la référence,
  // le contenu de l'onglet actif étant enveloppé dans son panneau (role="tabpanel").
  const fenetre = h(Tabs.Root, { asChild: true, value: tab, onValueChange: (v: string) => p.onTab?.(v as OngletReglages) },
    h('section', { className: cx('ag-ts', 'ag-glass', 'ag-glass-strong'), ...(dansReglages ? {} : { role: 'dialog', 'aria-label': p.title }) },
      h('header', { className: 'ag-ts-head' }, dansReglages ? h(Dialog.Title, { asChild: true }, titre) : titre,
        h('button', { type: 'button', className: 'ag-page-x', 'aria-label': p.closeLabel, onClick: p.onClose }, h(Icon, { name: 'close', size: 14 }))),
      h(Tabs.List, { className: cx('ag-seg', 'ag-glass-card'), 'aria-label': p.title }, onglets),
      h(Tabs.Content, { value: tab, tabIndex: undefined }, section()),
      pied()));
  if (!dansReglages) return fenetre;
  // Dans un DataTable : contenu de sa fenêtre Radix (non modale), focus initial sur l'onglet actif.
  return h(Dialog.Content, {
    asChild: true, 'aria-describedby': undefined,
    onOpenAutoFocus: (e: Event) => {
      e.preventDefault();
      (e.currentTarget as HTMLElement | null)?.querySelector<HTMLElement>('[role="tab"][data-state="active"]')?.focus();
    },
  }, fenetre);
}
